import React, { FC } from "react";
import { observer } from "mobx-react";
import { Box, Tooltip, CircularProgress } from "@mui/material";

import { TbPinned, TbWorldShare } from "react-icons/tb";
import { ChatIcon } from "../../icons";

import { useAppStore } from "../../../hooks/useAppStore";
import { SIDEBAR_TAB_TYPES } from "../../../types/sidebar";
import { useCapabilities } from "../../../contexts/permissions/useCapabilities";

interface SidebarChatTabsProps {
  sidebarSelectedTab: SIDEBAR_TAB_TYPES;
  setSidebarSelectedTab: (sidebarSelectedTab: SIDEBAR_TAB_TYPES) => void;
  loading?: boolean;
}

const ICON_ONLY_THRESHOLD = 290;

const TABS = [
  { id: SIDEBAR_TAB_TYPES.all, label: "Owned", Icon: ChatIcon },
  { id: SIDEBAR_TAB_TYPES.pinned, label: "Pinned", Icon: TbPinned },
  { id: SIDEBAR_TAB_TYPES.shared, label: "Shared", Icon: TbWorldShare },
];

const SidebarChatTabs: FC<SidebarChatTabsProps> = ({
  sidebarSelectedTab,
  setSidebarSelectedTab,
  loading,
}) => {
  const { isCollapsed, isSidebarMobile, sidebarWidth } = useAppStore();
  const { showSharedChats } = useCapabilities();

  if (isCollapsed || isSidebarMobile) return null;

  const iconsOnly = sidebarWidth < ICON_ONLY_THRESHOLD;

  return (
    <Box sx={{ px: 2, pb: 1.5, flexShrink: 0 }}>
      <Box
        role="tablist"
        sx={{
          display: "flex",
          bgcolor: "rgba(0, 0, 0, 0.06)",
          borderRadius: "8px",
          p: "3px",
          gap: "2px",
        }}
      >
        {TABS.map(({ id, label, Icon }) => {
          if (!showSharedChats && id === SIDEBAR_TAB_TYPES.shared) {
            return null;
          }
          const isActive = sidebarSelectedTab === id;

          return (
            <Tooltip
              key={id}
              title={iconsOnly ? label : ""}
              placement="bottom"
              arrow
            >
              <Box
                role="tab"
                component="button"
                aria-selected={isActive}
                onClick={() => setSidebarSelectedTab(id)}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  py: "5px",
                  px: 1.5,
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: isActive ? 500 : 400,
                  color: isActive ? "text.primary" : "text.secondary",
                  bgcolor: isActive ? "white" : "transparent",
                  boxShadow: isActive
                    ? "0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)"
                    : "none",
                  transition:
                    "background-color 0.15s ease, box-shadow 0.15s ease",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                  "&:hover": {
                    bgcolor: isActive ? "white" : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                {id === SIDEBAR_TAB_TYPES.all &&
                sidebarSelectedTab !== SIDEBAR_TAB_TYPES.all &&
                loading ? (
                  <CircularProgress size={14} thickness={5} color="inherit" />
                ) : (
                  <Icon size={14} />
                )}
                {!iconsOnly && label}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default observer(SidebarChatTabs);
